import { createForm } from "@tanstack/solid-form";
import { z } from "zod";
import { useI18n } from "@/api/i18n";
import { FieldInfo } from "@/components/ui/input";
import { TextArea } from "@/components/ui/textarea";
import { TextFieldRoot } from "@/components/ui/textfield";
import { useOnboardingStore } from "../../context/OnboardingContext";
import { OnboardingDescription, OnboardingTitle } from "../shared/text";

const MAX_CHARS = 1000;

const schemaValidation = z
  .string()
  .min(100, "Summary must be at least 100 characters")
  .max(MAX_CHARS, `Max ${MAX_CHARS} characters`);

const summaryDialogSchema = z.object({
  summary: schemaValidation,
});

export const Summary = () => {
  const [store, setStore] = useOnboardingStore();
  const { t } = useI18n();

  const form = createForm(() => ({
    defaultValues: {
      summary: store.summary.value,
    },
  }));

  return (
    <div class="flex flex-col gap-8 w-full">
      <div class="flex flex-col gap-4">
        <OnboardingTitle>{t("onboarding.steps.summary.title")}</OnboardingTitle>
        <OnboardingDescription>
          {t("onboarding.steps.summary.description")}
        </OnboardingDescription>
      </div>
      <div class="flex gap-2 justify-between items-end">
        <div class="flex flex-col gap-3 w-full md:w-2/3">
          <form.Field
            name="summary"
            validators={{
              onChange: summaryDialogSchema.shape.summary,
              onBlur: ({ value }) => {
                const validatedData = summaryDialogSchema.safeParse({
                  summary: value,
                });
                if (!validatedData.success) {
                  setStore("summary", {
                    value: value,
                    error: validatedData.error.issues[0].message,
                  });

                  return validatedData.error.issues[0].message;
                }

                setStore("summary", {
                  value: validatedData.data.summary,
                  error: null,
                });
              },
            }}
          >
            {(field) => (
              <TextFieldRoot>
                <TextArea
                  placeholder={t("onboarding.steps.summary.placeholder")}
                  value={field().state.value ?? ""}
                  onInput={(e) => field().handleChange(e.currentTarget.value)}
                  onBlur={field().handleBlur}
                  class="w-full min-h-[400px]"
                  classList={{
                    "border-red-500": field().state.meta.errors.length > 0,
                  }}
                />
                <div class="flex justify-between items-center">
                  <FieldInfo
                    field={field()}
                    helpText={t("onboarding.steps.summary.helpText")}
                  />
                  <p
                    class="text-sm text-muted-foreground text-right"
                    classList={{
                      "text-red-500": field().state.meta.errors.length > 0,
                    }}
                  >
                    {field().state.value?.length ?? 0} / {MAX_CHARS}
                  </p>
                </div>
              </TextFieldRoot>
            )}
          </form.Field>
        </div>
      </div>
    </div>
  );
};
